import { useAppState } from '.';
import { TherapySession } from '../types';
import { useCallback } from 'react';
import { isHeadwayOrganization } from '../utils/customization/headway';
import { hasOrgAdminPermissions } from '../utils/userUtils';
import { GQLContractWithoutClientConsentsAndClientOverviewFragment } from '@upheal/graphql/definitions/schema.generated';
import { isOptionsMDOrganization } from '../utils/customization/options-md';
import { isCartwheelOrganization } from '../utils/customization/cartwheel';

export const usePermissions = () => {
    const { organizations, user } = useAppState();

    const isOrgAdmin = hasOrgAdminPermissions({ user });

    const canScheduleSession = !user?.sharedClientsModeEnabled || isOrgAdmin;

    return {
        canSearchAllClients: Boolean(
            user?.sharedClientsModeEnabled ||
                (user?.orgAdminSharedClientsModeEnabled && isOrgAdmin),
        ),
        canModifyContract: !user?.sharedClientsModeEnabled || isOrgAdmin,
        canAddContract:
            (!user?.sharedClientsModeEnabled &&
                !isHeadwayOrganization(organizations)) ||
            isOrgAdmin,
        canCreateTemplate: !isCartwheelOrganization(organizations),
        canReassignContract: !user?.sharedClientsModeEnabled && isOrgAdmin,
        canDeleteSession: useCallback(
            (session: TherapySession) =>
                isOrgAdmin ||
                (session.providerId && user?.id === session.providerId),
            [user?.id, isOrgAdmin],
        ),
        canEvaluateAIForSession: useCallback(
            (session: TherapySession) => session.providerId === user?.id,
            [user?.id],
        ),
        canCreateSessionForContract: useCallback(
            (
                contract: GQLContractWithoutClientConsentsAndClientOverviewFragment,
            ) => {
                return (
                    user?.sharedClientsModeEnabled ||
                    (user && contract.providerUserIds.includes(user.id))
                );
            },
            [user],
        ),
        canGenerateTreatmentPlanForContract: useCallback(
            (
                contract: GQLContractWithoutClientConsentsAndClientOverviewFragment,
            ) => {
                return (
                    user?.sharedClientsModeEnabled ||
                    (user && contract.providerUserIds.includes(user.id))
                );
            },
            [user],
        ),
        canScheduleSession,
        canScheduleSessionForContract: useCallback(
            (
                contract: GQLContractWithoutClientConsentsAndClientOverviewFragment,
            ) => {
                return (
                    canScheduleSession &&
                    (user?.sharedClientsModeEnabled ||
                        (user && contract.providerUserIds.includes(user.id)))
                );
            },
            [canScheduleSession, user],
        ),
        canEditMyProfile: !user?.sharedClientsModeEnabled || isOrgAdmin,
        canCreateAnonymousSession:
            !isHeadwayOrganization(organizations) &&
            !isOptionsMDOrganization(organizations),
        canPurchasePlan: isOrgAdmin,
        canImportFromExternalEhr: isOrgAdmin,
    };
};
